import { FeatureRadio } from '@/components/chariot-ui';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { useUpdateFlags } from '@/hooks/useFlags';
import { useMy } from '@/hooks/useMy';
import { FlagType, STATIC_IP_ADDRESS } from '@/types';

interface SourceIPFormProps {
  customerEmail?: string;
  onClose?: () => void;
}

/**
 * Source IP configuration modal form
 *
 * Allows users to switch between dynamic (rotating) and static IP addresses
 * for security scanning operations. Shows conditional warning when switching
 * from static to dynamic mode.
 */
export function SourceIPForm({ customerEmail, onClose }: SourceIPFormProps) {
  const { data: flags } = useMy({ resource: 'flag' }, { email: customerEmail });

  const flagsData = (flags || []).map(flag => flag.name);
  const initialMode = flagsData.includes(FlagType.STATIC)
    ? 'static'
    : 'dynamic';

  const [selectedMode, setSelectedMode] = useState<'dynamic' | 'static'>(
    initialMode
  );
  const [serverMode, setServerMode] = useState<'dynamic' | 'static'>(
    initialMode
  );
  const { mutateAsync: updateFlags, isPending } = useUpdateFlags({
    email: customerEmail,
  });

  // Sync state with API data when it arrives
  useEffect(() => {
    setSelectedMode(initialMode);
    setServerMode(initialMode);
  }, [initialMode]);

  const hasChanged = selectedMode !== serverMode;
  const showWarning = serverMode === 'static' && selectedMode === 'dynamic';

  const handleSubmit = async () => {
    if (!hasChanged) {
      onClose?.();
      return;
    }

    if (selectedMode === 'static') {
      await updateFlags({
        flagsToAdd: ['static'],
        successMessage: 'Source IP set to static',
      });
    } else {
      await updateFlags({
        flagsToDelete: ['static'],
        successMessage: 'Source IP set to dynamic',
      });
    }

    onClose?.();
  };

  return (
    <div className="flex flex-col gap-6" data-testid="source-ip-form">
      {/* Context Description */}
      <p className="text-chariot-text-secondary text-sm leading-relaxed">
        Choose how source IP addresses will be managed for your scanning
        operations. This setting affects how target systems will see incoming
        scan traffic.
      </p>

      {/* Radio Options */}
      <div className="flex flex-col gap-3">
        <FeatureRadio
          id="source-ip-dynamic"
          checked={selectedMode === 'dynamic'}
          title="Dynamic (Recommended)"
          description="Source IP addresses are subject to change over time. Dynamic IP addresses are useful for working around blacklisting and avoiding detection by rotating through different IP ranges."
          onChange={() => setSelectedMode('dynamic')}
          disabled={isPending}
          className="border-chariot-stroke hover:border-chariot-stroke-hover border transition-colors"
          data-testid="source-ip-radio-dynamic"
        />

        <FeatureRadio
          id="source-ip-static"
          checked={selectedMode === 'static'}
          title={`Static (${STATIC_IP_ADDRESS})`}
          description="Source IP addresses will remain the same over time. Static IP addresses are useful for whitelisting activity as non-malicious in firewalls, IDS/IPS systems, and security monitoring tools."
          onChange={() => setSelectedMode('static')}
          disabled={isPending}
          className="border-chariot-stroke hover:border-chariot-stroke-hover border transition-colors"
          data-testid="source-ip-radio-static"
        />
      </div>

      {/* Conditional Warning */}
      {showWarning && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/20">
          <svg
            className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-500"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
              Firewall rules may need updating
            </p>
            <p className="text-xs text-amber-800 dark:text-amber-300">
              Switching to dynamic IPs means scan traffic will come from
              different addresses. You may need to update firewall rules or IP
              whitelists.
            </p>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-2 pt-2">
        <Button
          onClick={onClose}
          variant="secondary"
          size="sm"
          disabled={isPending}
          data-testid="source-ip-cancel-button"
        >Cancel</Button>
        <Button
          onClick={handleSubmit}
          size="sm"
          disabled={isPending || !hasChanged}
          className="min-w-[120px]"
          data-testid="source-ip-apply-button"
        >{isPending ? 'Saving...' : 'Save'}</Button>
      </div>
    </div>
  );
}
